import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from 'hooks/usePermissions';
import { Shield, AlertTriangle, Home } from 'lucide-react';
import DashboardRedirect from './DashboardRedirect';

const EnhancedProtectedPage = ({ 
  children, 
  requiredPermissions = [], 
  fallbackPath = '/dashboard',
  showAccessDenied = true,
  pageName = 'esta página',
  useAllPermissions = false
}) => {
  const { hasAnyPermission, hasAllPermissions, getUserPermissions } = usePermissions();

  // Se não há permissões requeridas, permitir acesso
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return children;
  }

  // Verificar se o usuário tem as permissões necessárias
  const hasAccess = useAllPermissions 
    ? hasAllPermissions(requiredPermissions)
    : hasAnyPermission(requiredPermissions);
  
  
  if (!hasAccess) {
    // Se useAllPermissions é true e é o dashboard principal, redirecionar para o primeiro dashboard acessível
    if (useAllPermissions && pageName === 'Dashboard Principal') {
      return <DashboardRedirect />;
    }
    
    if (showAccessDenied) {
      const userPermissions = getUserPermissions();
      
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <Shield className="h-8 w-8 text-red-600" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Acesso Negado
            </h1>
            
            <p className="text-gray-600 mb-4">
              Você não tem permissão para acessar {pageName}.
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="text-sm font-medium text-yellow-800 mb-1">
                    Permissões necessárias:
                  </h3>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    {requiredPermissions.map(permission => (
                      <li key={permission} className="flex items-center">
                        <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                        {permission}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {userPermissions.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-blue-800 mb-2">
                  Suas permissões atuais:
                </h3>
                <div className="text-xs text-blue-700">
                  {userPermissions.slice(0, 5).join(', ')}
                  {userPermissions.length > 5 && ` e mais ${userPermissions.length - 5} permissões`}
                </div>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={() => window.history.back()}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Voltar
              </button>
              <button
                onClick={() => window.location.href = fallbackPath}
                className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center"
              >
                <Home className="h-4 w-4 mr-2" />
                Ir para Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

export default EnhancedProtectedPage;



